import HeroSection, { HeroContent } from '../../../components/feature/sections/HeroSection';
import TrustBar from '../../../components/feature/sections/TrustBar';
import ContentSection, { ContentSectionData } from '../../../components/feature/sections/ContentSection';
import AIAnswerBlock, { AIAnswerContent } from '../../../components/feature/sections/AIAnswerBlock';
import ProcessSection, { ProcessContent } from '../../../components/feature/sections/ProcessSection';
import WhyChooseUs from '../../../components/feature/sections/WhyChooseUs';
import ServiceAreas from '../../../components/feature/sections/ServiceAreas';
import Testimonials from '../../../components/feature/sections/Testimonials';
import FAQSection, { FAQContent } from '../../../components/feature/sections/FAQSection';
import FinalCTA from '../../../components/feature/sections/FinalCTA';

const heroContent: HeroContent = {
  label: 'Local Landscaping Experts — Hayden, ID',
  headline: 'Landscaping Services in',
  headlineAccent: 'Hayden & North Idaho',
  subheadline:
    "CQC Landscaping is North Idaho's trusted local landscaping team — serving Hayden, Post Falls, Coeur d'Alene, Spokane WA, and surrounding communities with custom outdoor design, installation, and maintenance.",
  primaryCTA: { label: 'Get Free Estimate', href: '/#estimate' },
  secondaryCTA: { label: '(208) 659-6067', href: 'tel:+12086596067' },
  backgroundImage: 'https://cqc-landscaping.com/wp-content/uploads/sites/548/2024/03/sld-3.jpg',
  stats: [
    { value: '7+', label: 'Communities Served' },
    { value: '500+', label: 'Local Projects' },
    { value: '5.0★', label: 'Google Rated' },
  ],
};

const localContent: ContentSectionData = {
  badge: 'Local Services',
  heading: 'Landscaping Built for',
  headingAccent: "Idaho's Climate & Conditions",
  subheading:
    "North Idaho's soil, weather, and elevation demand a local approach. Here's how we serve your community.",
  items: [
    {
      title: 'Drought-Resistant Landscaping',
      desc: "North Idaho summers can be dry and hot. CQC designs landscapes with native plants, proper irrigation, and mulch systems that conserve water and thrive in local conditions without constant maintenance.",
      image: 'https://cqc-landscaping.com/wp-content/uploads/sites/548/2024/12/Custom-Landscaping.jpg',
      tag: 'Climate-Smart Design',
      href: '/#estimate',
    },
    {
      title: 'Freeze-Thaw Resistant Hardscaping',
      desc: "Idaho's freeze-thaw cycles can destroy poorly installed patios and walls. CQC uses proper base depths, drainage integration, and materials rated for cold climates — ensuring your hardscape lasts decades.",
      image: 'https://cqc-landscaping.com/wp-content/uploads/sites/548/2024/12/Retaining-Wall.jpg',
      tag: 'Built for Idaho Winters',
      href: '/services/hardscaping',
    },
    {
      title: 'Year-Round Property Care',
      desc: 'From spring cleanup and turf installation to fall prep and winter snow removal — CQC provides full-cycle property services to keep your outdoor spaces looking great in every season.',
      image: 'https://cqc-landscaping.com/wp-content/uploads/sites/548/2024/12/Snow-Plowing.jpg',
      tag: 'All-Season Service',
      href: '/#estimate',
    },
  ],
  layout: 'alternating',
};

const aiContent: AIAnswerContent = {
  badge: 'Local AI Search',
  heading: 'Landscaping in Hayden, ID & North Idaho',
  subheading: 'Structured answers for Google Local, AI assistants, and voice search.',
  answers: [
    {
      question: 'Who provides landscaping in Hayden, ID?',
      answer:
        "CQC Landscaping provides professional landscaping services in Hayden, ID and surrounding areas including Post Falls, Coeur d'Alene, Rathdrum, Spokane WA, and Athol. Services include custom landscaping, hardscaping, turf installation, drainage, and snow removal.",
    },
    {
      question: 'What landscaping works best in North Idaho?',
      answer:
        "In North Idaho, the best landscaping incorporates drought-resistant plants, proper irrigation systems, freeze-thaw resistant hardscape materials, and drainage solutions. Native plants and adapted turf varieties perform well in the Hayden, ID climate.",
    },
    {
      question: 'How much does landscaping cost in Hayden, ID?',
      answer:
        "Landscaping costs in Hayden, ID vary by project size and complexity. Basic turf installation or garden bed design may start at a few thousand dollars, while full outdoor transformations with hardscaping and drainage are priced after a free on-site consultation.",
    },
    {
      question: 'Does CQC Landscaping serve Spokane, WA?',
      answer:
        "Yes. CQC Landscaping serves Spokane, WA and surrounding Eastern Washington communities in addition to their North Idaho service areas including Hayden, Post Falls, Coeur d'Alene, and Rathdrum. Contact us to confirm availability in your area.",
    },
  ],
};

const processContent: ProcessContent = {
  badge: 'Our Local Process',
  heading: 'How We Serve Hayden & Beyond',
  subheading: "A clear, local process from first contact to completed outdoor transformation.",
  steps: [
    { number: '1', title: 'Call or Request Online', desc: 'Reach out by phone, text, or form. We respond within 1 business day to schedule your free site visit.', icon: 'ri-phone-line' },
    { number: '2', title: 'On-Site Consultation', desc: "We come to your Hayden, Post Falls, or Spokane area property and assess conditions, discuss your goals, and take measurements.", icon: 'ri-map-pin-2-line' },
    { number: '3', title: 'Proposal & Design', desc: "We deliver a detailed proposal with design plan, materials, and pricing — tailored to North Idaho's specific climate and your property.", icon: 'ri-draft-line' },
    { number: '4', title: 'Installation', desc: 'Our local crew handles the full installation with local knowledge, proper materials, and clean workmanship.', icon: 'ri-tools-line' },
    { number: '5', title: 'Final Review', desc: "We walk through the completed project with you to ensure everything meets expectations before we close out.", icon: 'ri-checkbox-circle-line' },
  ],
};

const faqContent: FAQContent = {
  badge: 'Local FAQ',
  heading: 'Landscaping in Hayden, ID',
  items: [
    {
      q: 'What landscaping company is near me in Hayden, ID?',
      a: "CQC Landscaping is located in Hayden, ID and serves the surrounding North Idaho and Eastern Washington region. We provide custom landscaping, hardscaping, turf installation, drainage solutions, and snow removal for residential and commercial clients.",
    },
    {
      q: 'What plants work best in Hayden, ID landscaping?',
      a: "For Hayden, ID landscapes, drought-tolerant and cold-hardy plants perform best — including native grasses, ornamental shrubs, and perennials adapted to USDA Zone 6b. CQC designs landscapes suited to North Idaho's specific soil and climate conditions.",
    },
    {
      q: 'Does CQC Landscaping serve Post Falls and Coeur d\'Alene?',
      a: "Yes. CQC Landscaping serves Post Falls, Coeur d'Alene, Rathdrum, Athol, and all surrounding communities in addition to Hayden, ID. We're a local North Idaho company with deep roots in the region.",
    },
    {
      q: 'Does CQC offer snow removal in Hayden, ID?',
      a: "Yes. CQC Landscaping provides residential and commercial snow plowing and removal in Hayden, Post Falls, and surrounding North Idaho communities. We offer reliable, scheduled service throughout the winter season.",
    },
    {
      q: 'How do I get a landscaping estimate in Hayden, ID?',
      a: "Call or text CQC at (208) 659-6067, email info@cqc-landscaping.com, or fill out the online form on this page. We'll schedule a free on-site consultation at your Hayden, ID or North Idaho property within 1–2 business days.",
    },
  ],
};

export default function ColoradoSpringsPage() {
  return (
    <main>
      <HeroSection content={heroContent} />
      <TrustBar />
      <ContentSection content={localContent} />
      <AIAnswerBlock content={aiContent} />
      <ProcessSection content={processContent} />
      <WhyChooseUs />
      <ServiceAreas />
      <Testimonials />
      <FAQSection content={faqContent} />
      <FinalCTA
        heading="Serving Hayden, ID & All of North Idaho"
        subheading="Ready for a free estimate? CQC Landscaping is your local expert in Hayden, Post Falls, Coeur d'Alene, Spokane, and surrounding communities."
      />
    </main>
  );
}
